import { useRef, useState } from "react";
import { motion } from "motion/react";
import { ArrowRight, Loader2 } from "lucide-react";

import { type Message } from "shared/apiClient";
import { getText, titleCase } from "shared/lib";
import { type QuestionData } from "@/components/wizard/lib";
import { animationVariants } from "../lib";

import { createMessageEvent, useChannel } from "shared/channel";
import {
  CaptureEventType,
  useAccount,
  useCancelOrCompleteApplication,
  useCaptureEvent,
  useShouldRenderSideBySide,
} from "@/hooks";

import { Button, Separator, Textarea } from "shared/components";
import { QuestionLoader } from "@/components/wizard/questionLoader";
import { SiteOverview } from "@/components/siteOverview";
import { SiteOverviewMap } from "@/components/siteOverview/SiteOverviewMap";

export interface SiteOverviewStepProps {
  disabled?: boolean;
  message: Message;
  showLoading?: boolean;
}

export function SiteOverviewStep({
  disabled,
  message,
  showLoading,
}: SiteOverviewStepProps) {
  const { captureEvent } = useCaptureEvent();

  const questionData = message.question_data as QuestionData;

  const { channel } = useChannel();

  const { application, conversation } = useAccount();

  const shouldRenderSideBySide = useShouldRenderSideBySide();

  const [submitting, setSubmitting] = useState(false);

  const changeAddressButtonRef = useRef<HTMLButtonElement>(null);

  const [freeText, setFreeText] = useState("");

  const {
    isPending: cancelOrCompleteApplicationLoading,
    mutate: cancelOrCompleteApplication,
  } = useCancelOrCompleteApplication({});

  const handleClickNext = () => {
    if (channel && conversation?.id) {
      setSubmitting(true);

      const messageDescriptor = createMessageEvent({
        conversation_id: conversation.id,
        text: freeText,
      });

      console.log("<SiteOverviewStep> handleClickNext()", messageDescriptor);
      channel.sendMessage(messageDescriptor);

      captureEvent(CaptureEventType.USER_ANSWERED_QUESTION, {
        messageId: message.id,
      });
    }
  };

  const handleClickChangeAddress = () => {
    console.log("<SiteOverviewStep> handleClickChangeAddress()");

    cancelOrCompleteApplication();
  };

  const renderFooter = () => {
    return (
      <motion.div
        variants={animationVariants}
        animate={["buttons"]}
        className="flex flex-col items-end gap-2"
      >
        <Button
          data-testid="next-button"
          disabled={
            cancelOrCompleteApplicationLoading ||
            submitting ||
            disabled ||
            freeText.length === 0
          }
          onClick={handleClickNext}
          size="sm"
        >
          {submitting || showLoading ? (
            <>
              <Loader2 className="mr-3 animate-spin" data-testid="submitting" />
            </>
          ) : null}
          {getText(questionData.next_button_label || "Describe your proposal")}
          <ArrowRight />
        </Button>
      </motion.div>
    );
  };

  if (showLoading) {
    return <QuestionLoader />;
  }

  return (
    <div
      className="wizard-step-transition text-bodylg flex h-full w-full flex-col gap-5 md:max-w-[600px]"
      data-testid="step-container-multi-select"
    >
      <div className="flex flex-col">
        <h2 className="text-headingxl text-textPrimary">
          {getText("We've retrieved planning details for ")}
        </h2>

        <h2 className="text-headingxl text-textPrimary font-semibold">
          {titleCase(conversation?.address || "")}
        </h2>
      </div>

      {shouldRenderSideBySide && (
        <SiteOverviewMap address={conversation?.address_data} />
      )}

      {!shouldRenderSideBySide && (
        <>
          <SiteOverview address={conversation?.address_data} />

          <Separator />
        </>
      )}

      <div className="flexflex-col gap-1">
        <h4 className="text-bodylg text-textPrimary">
          {questionData?.question_title ||
            getText("How is the lot currently being used?")}
        </h4>

        <p className="text-bodymd text-textSubdued">
          {getText(
            questionData?.question_subtitle ||
              "E.g. it's my house, a farm for horses, I run my small business there.",
          )}
        </p>
      </div>

      <Textarea
        autoFocus
        disabled={submitting || showLoading || disabled}
        onChange={(event) => {
          setFreeText(event.target.value);
        }}
        placeholder={getText("Answer in your own words")}
        value={freeText}
      />

      {renderFooter()}
    </div>
  );
}
